import React, {ReactElement, useMemo, useState} from "react";
import {Question} from "../../models/question";
import {Choice} from "../../models/answer";
import {ConsentDecision, getConsentChoices} from "../../utility/consent";
import {useScrollGate} from "../../hooks/useScrollGate";
import {PdfViewer, PdfViewerStatus} from "../pdf-viewer";
import {Button} from "../button";
import {Spinner} from "../spinner";
import styles from "./consent-question.module.scss";

export interface ConsentQuestionProps {
	question: Question;
	/**
	 * Called when the respondent presses one of the two buttons.
	 */
	onDecision?: (choice: Choice, decision: ConsentDecision) => void;
	/**
	 * Disables both buttons while an answer is being recorded.
	 */
	isSubmitting?: boolean;
}

/**
 * A consent question: the question text, the consent document, and the two buttons.
 * Accept is locked behind the scroll gate; Decline never is.
 */
const ConsentQuestion = ({question, onDecision, isSubmitting = false}: ConsentQuestionProps): ReactElement => {
	const choices = useMemo(() => getConsentChoices(question), [question]);
	const documentUrl = question.file?.url ?? null;

	const [status, setStatus] = useState<PdfViewerStatus>("loading");
	const [retryKey, setRetryKey] = useState(0);
	const [openedExternally, setOpenedExternally] = useState(false);
	// Only measure once every page is drawn, so an empty or half-loaded container never counts.
	// The component is keyed by question id, so a different document always starts fresh.
	const {ref: scrollRef, reached, recheck, reset} = useScrollGate({enabled: status === "ready"});

	const canAccept = !isSubmitting && Boolean(choices) && ((status === "ready" && reached) || openedExternally);
	const canDecline = !isSubmitting && Boolean(choices);

	const handleRetry = (): void => {
		reset();
		setRetryKey(key => key + 1);
	};

	const handleDecision = (decision: ConsentDecision): void => {
		if (!choices) return;
		onDecision?.(decision === "accept" ? choices.affirmative : choices.negative, decision);
	};

	const renderDocumentState = (): ReactElement | null => {
		if (status === "loading") {
			return (
				<div className={styles.overlay}>
					<Spinner />
				</div>
			);
		}
		if (status === "error") {
			return (
				<div className={styles.overlay}>
					<div className={styles.errorBox}>
						<p className={styles.errorText}>
							{documentUrl
								? "We couldn't display the consent document."
								: "The consent document isn't available yet. Please try again in a moment."}
						</p>
						<div className={styles.errorActions}>
							<Button variant="outlined" size="small" onClick={handleRetry}>
								Retry
							</Button>
							{documentUrl && (
								<a
									href={documentUrl}
									className={styles.openLink}
									target="_blank"
									rel="noreferrer noopener"
									onClick={() => setOpenedExternally(true)}
								>
									Open PDF in a new tab
								</a>
							)}
						</div>
					</div>
				</div>
			);
		}
		return null;
	};

	const renderHint = (): ReactElement | null => {
		if (canAccept || status !== "ready") return null;
		return <p className={styles.hint}>Scroll to the end of the document to enable “{choices?.affirmative.text}”.</p>;
	};

	return (
		<div className={styles.container}>
			<h2 className={styles.questionText}>{question.text}</h2>
			<div className={styles.documentFrame}>
				<div ref={scrollRef} className={styles.document} data-testid="consent-document">
					<PdfViewer url={documentUrl} retryKey={retryKey} onStatusChange={setStatus} onPageRendered={recheck} />
				</div>
				{renderDocumentState()}
			</div>
			{renderHint()}
			{!choices && <p className={styles.errorText}>This consent question is missing its Accept and Decline options.</p>}
			{choices && (
				<div className={styles.actions}>
					<Button
						variant="outlined"
						disabled={!canDecline}
						onClick={() => handleDecision("decline")}
						className={styles.button}
					>
						{choices.negative.text}
					</Button>
					<Button disabled={!canAccept} onClick={() => handleDecision("accept")} className={styles.button}>
						{choices.affirmative.text}
					</Button>
				</div>
			)}
		</div>
	);
};

export {ConsentQuestion};
